import React from 'react';
import { Box, Container, Grid, Stack, Typography, useTheme, alpha } from '@mui/material';
import { ArrowForward, Code, Security } from '@mui/icons-material';
import { HERO_ORBIT_ICONS, HERO_STATS } from '../../data/siteContent';
import { SECTION_SCROLL_MARGIN, scrollToSection } from '../../utils/scroll';
import { MotionBox, MotionButton, MotionTypography, slideIn, useMotionProps, useReducedMotion } from '../motion';

const ORBIT_POSITIONS = [
  { top: '14%', right: '12%' },
  { top: '58%', right: '6%' },
  { bottom: '14%', left: '16%' },
  { top: '22%', left: '6%' },
  { bottom: '32%', right: '22%' }
];

const Hero = () => {
  const theme = useTheme();
  const motionProps = useMotionProps();
  const reducedMotion = useReducedMotion();
  const orbitIcons = [Code, ...HERO_ORBIT_ICONS, Security];

  return (
    <Box
      id="home"
      component="section"
      aria-labelledby="hero-heading"
      sx={{
        scrollMarginTop: SECTION_SCROLL_MARGIN,
        minHeight: { xs: 'auto', md: '100vh' },
        pt: { xs: 12, md: 10 },
        pb: { xs: 8, md: 10 },
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: 'background.default'
      }}
    >
      {/* Layered aurora + technical grid: depth without introducing new hues. */}
      <Box aria-hidden sx={{ position: 'absolute', inset: 0, background: theme.surfaces.aurora }} />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: theme.surfaces.grid,
          backgroundSize: theme.surfaces.gridSize,
          maskImage: 'radial-gradient(ellipse 80% 60% at 50% 40%, #000 0%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 40%, #000 0%, transparent 75%)'
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 6, lg: 8 }} alignItems="center">
          <Grid size={{ xs: 12, lg: 7 }}>
            <MotionBox {...motionProps(slideIn('left'))}>
              {/* Status pill, in place of an emoji, so screen readers get clean text. */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1.25,
                  mb: 3,
                  px: 2,
                  py: 0.85,
                  borderRadius: 99,
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: 'success.main',
                    boxShadow: `0 0 0 3px ${alpha(theme.palette.success.main, 0.2)}`,
                    ...(reducedMotion
                      ? {}
                      : {
                          animation: 'ssiplPulse 2.4s ease-in-out infinite',
                          '@keyframes ssiplPulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.35 }
                          }
                        })
                  }}
                />
                <Typography
                  component="span"
                  sx={{ fontWeight: 700, fontSize: '0.82rem', color: 'primary.main', letterSpacing: 0.3 }}
                >
                  Established 2000 · 25 Years of Excellence
                </Typography>
              </Box>
            </MotionBox>

            <MotionTypography
              {...motionProps(slideIn('left', 0.1))}
              id="hero-heading"
              variant="h1"
              component="h1"
              sx={{ mb: 3, fontWeight: 800 }}
            >
              <Box component="span" sx={{ color: 'text.primary' }}>
                Software Systems
              </Box>
              <br />
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(120deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent'
                }}
              >
                International
              </Box>
            </MotionTypography>

            <MotionTypography
              {...motionProps(slideIn('left', 0.2))}
              variant="h5"
              component="p"
              color="text.secondary"
              sx={{ mb: 4, maxWidth: 620, lineHeight: 1.6, fontWeight: 400 }}
            >
              Leading Sri Lankan software company specializing in{' '}
              <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                enterprise solutions
              </Box>{' '}
              for local businesses. Transforming operations with proven technology and deep industry expertise.
            </MotionTypography>

            <MotionBox {...motionProps(slideIn('left', 0.3))}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 5 }}>
                <MotionButton
                  whileHover={reducedMotion ? undefined : { y: -2 }}
                  whileTap={reducedMotion ? undefined : { scale: 0.98 }}
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  onClick={() => scrollToSection('solutions')}
                  sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: '1.05rem', borderRadius: 3 }}
                >
                  Explore Solutions
                </MotionButton>
                <MotionButton
                  whileHover={reducedMotion ? undefined : { y: -2 }}
                  whileTap={reducedMotion ? undefined : { scale: 0.98 }}
                  variant="outlined"
                  size="large"
                  onClick={() => scrollToSection('contact')}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontWeight: 700,
                    fontSize: '1.05rem',
                    borderRadius: 3,
                    backgroundColor: alpha('#fff', 0.6),
                    backdropFilter: 'blur(8px)'
                  }}
                >
                  Get In Touch
                </MotionButton>
              </Stack>
            </MotionBox>

            <MotionBox {...motionProps(slideIn('left', 0.4))}>
              <Stack
                direction="row"
                spacing={{ xs: 3, sm: 5 }}
                flexWrap="wrap"
                useFlexGap
                divider={
                  <Box
                    aria-hidden
                    sx={{ width: '1px', alignSelf: 'stretch', backgroundColor: alpha(theme.palette.primary.main, 0.15) }}
                  />
                }
              >
                {HERO_STATS.map((stat) => (
                  <Box key={stat.label}>
                    <Typography
                      variant="h4"
                      component="p"
                      sx={{ fontWeight: 900, mb: 0.5, color: 'primary.main', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {stat.label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </MotionBox>
          </Grid>

          <Grid size={{ xs: 12, lg: 5 }} sx={{ display: { xs: 'none', lg: 'block' } }}>
            <MotionBox {...motionProps(slideIn('right', 0.2))} aria-hidden>
              <Box sx={{ position: 'relative', height: 480, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {/* Rotating conic ring behind the mark. */}
                {!reducedMotion && (
                  <MotionBox
                    animate={{ rotate: 360 }}
                    transition={{ duration: 28, repeat: Infinity, ease: 'linear' }}
                    sx={{
                      position: 'absolute',
                      width: 420,
                      height: 420,
                      borderRadius: '50%',
                      background: `conic-gradient(from 0deg, transparent 0deg, ${alpha(
                        theme.palette.primary.main,
                        0.35
                      )} 90deg, transparent 180deg, ${alpha(theme.palette.secondary.main, 0.3)} 270deg, transparent 360deg)`,
                      maskImage: 'radial-gradient(circle, transparent 62%, #000 63%, #000 65%, transparent 66%)',
                      WebkitMaskImage: 'radial-gradient(circle, transparent 62%, #000 63%, #000 65%, transparent 66%)'
                    }}
                  />
                )}

                <MotionBox
                  animate={reducedMotion ? undefined : { y: [-8, 8, -8] }}
                  transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
                  sx={{
                    width: 360,
                    height: 360,
                    borderRadius: '50%',
                    ...theme.surfaces.glass,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    boxShadow: `0 24px 80px ${alpha(theme.palette.primary.main, 0.18)}`
                  }}
                >
                  <Box
                    component="img"
                    src="/favicon.png"
                    alt=""
                    sx={{ width: 110, height: 110, objectFit: 'contain', filter: 'drop-shadow(0 6px 16px rgba(0,0,0,0.12))' }}
                  />

                  {!reducedMotion &&
                    orbitIcons.map((Icon, index) => (
                      <MotionBox
                        key={index}
                        animate={{ y: [-6, 6, -6] }}
                        transition={{ duration: 4 + index * 0.5, repeat: Infinity, ease: 'easeInOut', delay: index * 0.4 }}
                        sx={{
                          position: 'absolute',
                          ...ORBIT_POSITIONS[index],
                          width: 52,
                          height: 52,
                          borderRadius: 2.5,
                          background: alpha('#fff', 0.92),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'primary.main',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`
                        }}
                      >
                        <Icon />
                      </MotionBox>
                    ))}
                </MotionBox>
              </Box>
            </MotionBox>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Hero;
